"use client";

import { SDUIOnViewHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnViewHandler/SDUIOnViewHandler";
import {
  ActionLoggingEventParams,
  ComponentLoggingEventParams,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { getCssFromStyle } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/getCssFromTileStyle";
import { SDUITileRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/SDUITileRenderer";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { ILoggingAction } from "@faire/web-api--source/indigofair/data/ILoggingAction";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import React, { useMemo } from "react";

export const SDUIGridV2Tile: React.FC<{
  tile: ITile;
  adjacentTileTokens?: string[];
  tileRef?: React.RefObject<HTMLDivElement | null>;
  loggingData: ComponentLoggingEventParams;
  logTileBatchView: IGridComponent.ILogTileBatchView | undefined;
  rowSpacingPixels: number;
  shouldSetIsScrollingTopToFalseOnView?: boolean;
}> = ({
  tile,
  adjacentTileTokens,
  tileRef,
  loggingData,
  rowSpacingPixels,
  logTileBatchView,
  shouldSetIsScrollingTopToFalseOnView,
}) => {
  const actionContent = useMemo(
    () =>
      logTileBatchView && adjacentTileTokens
        ? adjacentTileTokens.join(" ")
        : undefined,
    [adjacentTileTokens, logTileBatchView]
  );

  const actionLoggingParams = useMemo(
    () =>
      ActionLoggingEventParams.build({
        ...loggingData,
        action_content: tile.logging_data?.instance_identifier,
        action_positions: tile.logging_data?.tile_position?.toString(),
        action_instance_identifier:
          tile.logging_data?.instance_identifier ?? "",
      }),
    [
      loggingData,
      tile.logging_data?.instance_identifier,
      tile.logging_data?.tile_position,
    ]
  );

  const onViewActions = useMemo(
    () => [
      ...tile.on_view,
      ...(logTileBatchView
        ? [
            IAction.build({
              log_event: ILoggingAction.build({
                event_name: logTileBatchView.event_name,
                parameters: logTileBatchView.parameters.concat(
                  actionContent ?? actionLoggingParams.action_content ?? []
                ),
              }),
            }),
          ]
        : []),
    ],
    [
      actionContent,
      actionLoggingParams.action_content,
      logTileBatchView,
      tile.on_view,
    ]
  );

  return (
    <SDUIOnViewHandler
      onViewActions={onViewActions}
      loggingData={actionLoggingParams}
      componentStyle={{
        height: "100%",
        padding: `${rowSpacingPixels / 2}px 0px`,
        ...getCssFromStyle(tile.style),
      }}
      ref={tileRef}
      logLegacyViewEvent
      logSDUIViewEvent
      shouldSetIsScrollingTopToFalseOnView={
        shouldSetIsScrollingTopToFalseOnView
      }
    >
      <SDUITileRenderer tileElement={tile} loggingData={loggingData} />
    </SDUIOnViewHandler>
  );
};
